import { APIRequestStatus } from 'core/constants/redux';
import { ReportBuilderTab } from 'core/models/report-builder/report-builder';
import { defaultPageValue } from 'core/constants/pagination';
import { ROOT_QUERY_KEY } from 'components/feature/Report/ReportSidebar/common/constants';
import { UserFilterPayloadType } from 'core/constants/filter';
import {
  IReportBuilderState, ReportBuilderActions, ReportBuilderSaveStatus, ReportType,
} from './report-builder-store.state';

const initialReportBuilderState: IReportBuilderState = {
  requestProcessing: {
    [ReportBuilderActions.INITIAL_SAVE_TEMPLATE]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.SAVE_REPORT_CONFIG]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_FILTERS]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_USER_FIELD_FILTERS]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_DIMENSIONS]:
      APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_MEASURES]:
      APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_FIELDS]:
      APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_PREVIEW_DATA]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.FILTER_DATA_LOAD_REQUEST]: {},
    [ReportBuilderActions.GET_REPORT_RAW_CONFIG_REQUEST]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_REPORT_RENDER_CONFIG_REQUEST]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_MEASURE_DRILLDOWN_CONFIG]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_DRILLDOWN_CONFIG]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.SAVE_DRILLDOWN_CONFIG]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_DEFAULT_DRILLDOWN_COLUMN]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_DRILLDOWN_COLUMN]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_RAW_TEMPLATE_DETAILS]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_USER_DIMENSIONS]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_DATA_SOURCES_REQUEST]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.SAVE_DATA_SOURCE_CHANGES_REQUEST_RESET]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.SAVE_DATA_SOURCE_CHANGES_REQUEST]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_DIMENSIONS_FOR_NAMESPACES]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_FILTERS_FOR_NAMESPACES]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_MEASURES_FOR_NAMESPACES]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_USER_FIELDS_FOR_NAMESPACES]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ADDITIONAL_USER_FILTERS_FOR_NAMESPACES]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.SUB_QUERY_FILTER_LOAD_DATA_REQUEST]: {},
    [ReportBuilderActions.GET_ADDITIONAL_FIELDS_FOR_NAMESPACES]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.SAVE_NESTED_JOINS_CONFIG]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_SUB_QUERY_PREVIEW_DATA]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_META_SELECTOR_OPTIONS]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_BUILDER]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_LIST_REPORTS_ADDITIONAL_COLUMNS_FOR_SUBBUILDER]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.REPORT_BUILDER_TYPE_CHANGE_REQUEST]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_DATA_SOURCES_FIELDS_REQUEST]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_ALL_REPORTS_FOR_LINKED_INSIGHTS]: APIRequestStatus.AboutToLoad,
    [ReportBuilderActions.GET_REPORT_CONFIG_FOR_LINKED_INSIGHTS]: {},
  },
  reportBuilderInfo: {
    additionalFilters: [],
    additionalUserFieldFilters: [],
    additionalDimensions: [],
    additionalMeasures: [],
    fields: [],
    additionalUserFields: [],
    allReports: [],
    reportConfigMapping: {},
  },
  dataSourceInfo: {
    dataSources: null,
  },
  updatedReportBuilderInfo: {
    selectedTemplate: {
      ReportConfig: null,
      Namespace: null,
      TemplateId: null,
      Type: null,
      FilterMetaInfo: null,
      Settings: null,
      TemplateMeta: null,
    },
    reportBuilderSavePayload: null,
    appliedDimensions: [],
    appliedMeasures: [],
    sortState: [],
    calculatedColumns: null,
    expressionStore: {},
    drilldownConfigInfo: {
      drilldownConfigMapping: {},
      drilldownColumnConfig: {},
      enableDrilldown: true,
      drilldownPagination: defaultPageValue,
    },
    entityGroupOptionsMap: {},
    entityGroupStore: {},
    customHierarchies: [],
    conditionalFormatting: {
      Enabled: true,
      Rules: [],
    },
    linkedReportsConfig: [],
    isCurrentlyEditingOrAddingFormattingRule: false,
  },
  assortedInfo: {
    isFormDisabled: true,
    selectedTabInfo: ReportBuilderTab.Data,
    reportId: '',
    settings: null,
    reportBuilderSaveStatus: ReportBuilderSaveStatus.DRAFT,
    isNestedQueryFormOpen: false,
    isDataSourceEditMode: false,
    activeSubQueryPanel: null,
    isDataSourceSidetrayExpanded: false,
    isDrilldownMeasuresPanelExpanded: false,
    isDrilldownFieldsPanelExpanded: false,
    isReportDataSourceSaved: false,
  },
  previewData: {
    data: null,
    mappedMetaFields: null,
  },
  filterBuilder: {
    appliedFilters: {},
    filterConfig: [],
    isUsersModified: false,
    filterMetaInfo: [],
    UserDropdownSelectType: UserFilterPayloadType.All,
    customFilterDefinitions: [],
  },
  previewBackUpInfo: {
    updatedReportBuilderInfo: null,
    filterBuilder: null,
  },
  reportEditInfo: {
    rawReportConfig: null,
    renderReportConfig: null,
    combineConfigLoaded: false,
    isEditing: false,
    temporaryReportId: '',
  },
  reportQueryBuilder: {
    SelectedQuery: ROOT_QUERY_KEY,
    SelectedQueryData: {
      additionalData: {
        AdditionalDimensions: [],
        AdditionalMeasures: [],
        AdditionalUserFields: [],
        AdditionalFilters: [],
        AdditionalUserFieldFilters: [],
        Fields: [],
      },
      updatedInfo: {
        filterBuilder: {
          appliedFilters: {},
          filterConfig: [],
          isUsersModified: false,
          filterMetaInfo: [],
          UserDropdownSelectType: UserFilterPayloadType.All,
          customFilterDefinitions: [],
        },
        sortState: [],
        AppliedDimensions: [],
        AppliedMeasures: [],
        ExpressionStore: {},
        CalculatedColumns: new Set(),
        Paging: null,
      },
      NameSpace: '',
      QueryJoinRelation: null,
      Type: ReportType.Summary,
    },
    DynamicQueryList: [],
    StaticQueryList: [],
    PreviewData: {
      data: null,
      mappedMetaFields: null,
      previewQueryId: '',
      previewBackupInfo: {
        previewInfo: null,
      },
    },
    DataSourceMapping: null,
    DataSourceMappingPayload: null,
    BreadcrumbData: [],
  },
};

export default initialReportBuilderState;
